/**
 * ModeSelectorPage — picks the user's working mode.
 * Mirrors Flutter `app/pages/mode_selector/mode_selector_page.dart`.
 *
 * 5 tiles: sender, driver, center, receiver, company.
 * The selection routes to that mode's home dashboard.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModeStore, type AppMode } from '@/stores/modeStore';

const TILES: Array<{
  mode: AppMode;
  titleKey: string;
  fallback: string;
  emoji: string;
  subtitleKey: string;
  subtitleFallback: string;
}> = [
  {
    mode: 'sender',
    titleKey: 'mode_sender',
    fallback: 'مرسِل',
    emoji: '📦',
    subtitleKey: 'mode_sender_subtitle',
    subtitleFallback: 'إرسال شحنة وتتبّعها',
  },
  {
    mode: 'driver',
    titleKey: 'mode_driver',
    fallback: 'سائق',
    emoji: '🚚',
    subtitleKey: 'mode_driver_subtitle',
    subtitleFallback: 'استلم شحنات وارفعها لرحلاتك',
  },
  {
    mode: 'center',
    titleKey: 'mode_center',
    fallback: 'مركز شحن',
    emoji: '🏢',
    subtitleKey: 'mode_center_subtitle',
    subtitleFallback: 'استلم وسلِّم في محلّك',
  },
  {
    mode: 'receiver',
    titleKey: 'mode_receiver',
    fallback: 'مستلِم',
    emoji: '📬',
    subtitleKey: 'mode_receiver_subtitle',
    subtitleFallback: 'تابع الشحنات الواردة إليك',
  },
  {
    mode: 'company',
    titleKey: 'mode_company',
    fallback: 'شركة شحن',
    emoji: '🏛️',
    subtitleKey: 'mode_company_subtitle',
    subtitleFallback: 'محلية أو دولية — كيان رسمي',
  },
];

export default function ModeSelectorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setMode } = useModeStore();

  function pick(mode: AppMode) {
    setMode(mode);
    navigate('/home', { replace: true });
  }

  return (
    <div className="min-h-screen bg-background px-4 py-12">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-primary mb-2 text-center">
          {t('mode_selector_title', 'كيف تريد استخدام كارواتس كارقو؟')}
        </h1>
        <p className="text-text-secondary text-center mb-10">
          {t('mode_selector_subtitle', 'يمكنك تبديل الوضع لاحقاً من القائمة')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map((tile) => (
            <button
              key={tile.mode}
              onClick={() => pick(tile.mode)}
              className="bg-surface border border-border rounded-2xl p-6 text-start hover:border-primary hover:shadow-md transition group"
            >
              <div className="text-4xl mb-3">{tile.emoji}</div>
              <div className="text-xl font-bold text-primary mb-1 group-hover:text-primary-light">
                {t(tile.titleKey, tile.fallback)}
              </div>
              <div className="text-sm text-text-secondary">
                {t(tile.subtitleKey, tile.subtitleFallback)}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
